import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  Grid,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  IconButton,
  DialogTitle,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  saveIncidentsForm,
  setSelectedIncidentInfo,
} from '../../../../store/incidentsReducer';
import { useDispatch } from 'react-redux';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import { useForm, FormProvider } from 'react-hook-form';
import FormSection from './FormSection';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import {
  getSectionsByPathSuffix,
  createShouldShowField,
  extractDefaultValuesFromSettings,
  getAllErrorPaths,
  checkIsExpandedAccordion,
  getActiveMenuStyles,
} from './utils';
import { IncidentContext } from './IncidentContext';
import BaseInfo from './BaseInfo';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { toast } from 'react-toastify';
import { BASE_FIRST_ID, INCIDENT_FIELDS_FOR_WATCHING } from './constants';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    background: '#2121214c',
  },
  '& .MuiPaper-root': {
    maxWidth: 1195,
    width: 1195,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    borderTop: 'none',
    scrollBehavior: 'smooth',
    '& p.title': {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '22px',
      // marginBottom: "20px",
    },
    '& p.sectionTitle': {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '18px',
      color: '#6B6B6B',
      marginBottom: '10px',
    },
    '& p.subTitle': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#6B6B6B',
    },
    '& p.text': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#000000df',
      marginTop: '10px',
      whiteSpace: 'wrap',
      wordBreak: 'break-all',
    },
    '& .unit-box': {
      minHeight: 145,
      background: '#F3F3F3B2',
      borderRadius: '4px',
      padding: '15px',
    },
    '& .unit-title': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.87)',
      marginBottom: '15px',
    },
    '& .unit-subtitle': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.57)',
      marginTop: '10px',
      marginBottom: '10px',
    },
    '& .unit-text': {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.87)',
      marginTop: '8px',
      '& span': {
        fontWeight: 600,
      },
    },
    '& .form-input input': {
      borderColor: '#E4E4E7',
    },
    '& .MuiAccordion-root': {
      margin: '24px 0 0',
      border: '1px solid #E1E1E1',
      borderRadius: '8px',
      width: '100%',
      maxWidth: '100%',
      boxShadow: 'none',
      '&:before': {
        display: 'none',
      },
      '& .MuiCollapse-root': {
        transitionDuration: '0.2s !important',
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiAccordionDetails-root': {
        padding: 0,
      },
    },
  },
  '& .MuiDialogActions-root': {
    '& .actionButton': {
      borderColor: '#212121',
      color: '#212121',

      '&.MuiButton-contained': {
        background: '#212121',
        color: 'white',
        '&:disabled': {
          background: 'gray',
        },
      },
      '&.success': {
        '&.MuiButton-contained': {
          background: '#75ec9f',
          color: '#000000dd',
        },
      },
    },
  },
}));

export const getFormSettings = async (params, signal) => {
  try {
    const response = await incidentsApi.formSettings(params, { signal });
    if (response.status === 200) {
      return response.data;
    } else throw response;
  } catch (err) {
    toast.error(err?.data?.message || 'Fetching error');
    console.log(err);
  }
};

export default function EditIncidentModal({ editIncident, setEditIncident }) {
  const {
    selectedIncidentInfo,
    formSettings,
    formSettingsFetching,
    formSaveFetching,
  } = useSelector((state) => state.incidentsReducer);
  const dispatch = useDispatch();
  const { info, settings: defaultSettings } = formSettings;

  // localSettings will change if the Incident type changes
  // use useLayoutEffect to set as a local variable
  // use useLayoutEffect instead of useEffect for fixing double re-rendering
  const [localSettings, setLocalSettings] = useState(defaultSettings);
  useLayoutEffect(() => {
    setLocalSettings(defaultSettings);
  }, [defaultSettings]);

  const [isStructureLoading, setIsStructureLoading] = useState(false);
  const defaultIncidentType = useRef({
    primary_incident_type: '',
    incident_types: '',
  });

  const [selectedSections, setSelectedSections] = useState([]);

  const onToggleExpand = useCallback((sectionName, isExpanded) => {
    setSelectedSections((prev) => {
      if (isExpanded) {
        // set sectionName as last in any case
        // menu use last item as current
        return [...prev.filter((name) => name !== sectionName), sectionName];
      }

      const allRelatedElementsInAccordion = [
        sectionName,
        ...getSectionsByPathSuffix(prev, sectionName),
      ];

      return prev.filter(
        (name) => !allRelatedElementsInAccordion.includes(name)
      );
    });
  }, []);

  const onTransitionExitedHandler = () => {
    dispatch(setSelectedIncidentInfo(null));
  };

  // use onse - for init data
  // then use logic from useEffect below (with getFormSettings) for update values and LocalSettings
  const values = useMemo(() => {
    if (!defaultSettings || !info) return {};
    return extractDefaultValuesFromSettings(defaultSettings, info);
  }, [defaultSettings, info]);

  const form = useForm({
    values,
    mode: 'onChange',
  });

  const { handleSubmit, reset, watch } = form;

  const [primary_incident_type, incident_types] = watch(
    INCIDENT_FIELDS_FOR_WATCHING
  );

  // update form structures
  // depend on primary_incident_type and incident_types
  useEffect(() => {
    const abortController = new AbortController();

    if (!selectedIncidentInfo) {
      return;
    }
    if (!primary_incident_type && !incident_types?.length) {
      return;
    }

    // check with default values
    if (
      values?.base &&
      primary_incident_type === values.base.primary_incident_type &&
      incident_types?.join('') === values.base.incident_types?.join('')
    ) {
      return;
    }

    // check with previous values
    if (
      primary_incident_type ===
        defaultIncidentType.current.primary_incident_type &&
      incident_types?.join('') ===
        defaultIncidentType.current.incident_types?.join('')
    ) {
      return;
    }

    const params = { incident: selectedIncidentInfo.id };

    if (primary_incident_type) {
      params.primary_incident_type = primary_incident_type;
    }

    if (!!incident_types?.length) {
      params.incident_types = incident_types;
    }

    defaultIncidentType.current = {
      primary_incident_type,
      incident_types,
    };
    // set start loading
    setIsStructureLoading(true);

    getFormSettings(params, abortController.signal).then((formSettings) => {
      if (!formSettings) {
        return;
      }
      // save form values with the new structure
      const formValues = form.getValues();
      reset(
        extractDefaultValuesFromSettings(formSettings.settings, formValues)
      );

      // set new settings (structure)
      setLocalSettings(formSettings.settings);
      // set finish loading
      setIsStructureLoading(false);
    });

    return () => {
      abortController.abort();
    };
  }, [primary_incident_type, incident_types]);

  const closeHandler = () => {
    setEditIncident(false);

    reset(
      {},
      {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false, // <--- resets submitCount
      }
    );

    setSelectedSections([]);
  };

  const onSubmit = (data) => {
    if (selectedIncidentInfo && data) {
      dispatch(saveIncidentsForm(selectedIncidentInfo.id, data, closeHandler));
    }
  };

  const scrollToError = (elementName) => {
    const field = document.getElementById(elementName);

    if (field) {
      field.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleMenuItemClick = (sectionName) => {
    // Find the accordion element by ID
    const accordionElement = document.getElementById(sectionName);

    if (accordionElement) {
      onToggleExpand(sectionName, true);

      // Scroll to the accordion with a small delay to ensure accordion expansion has started
      setTimeout(() => {
        accordionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  const [isValidationPassed, setIsValidationPassed] = useState(null);
  const handleValidation = () => {
    // use trigger for rerender check required fields!
    // validation has problems - dependent fields in the group do not know that they have become required (or other rules) until the component is rerendered
    // A possible solution is to build a dependency tree outside the React components,
    // and a reverse tree that has a dependency in the keys, and in the values the IDs of the fields that depend on it (and update them)
    setIsValidationPassed(null);

    setTimeout(() => {
      handleSubmit(
        () => {
          setIsValidationPassed(true);
        },
        (errors) => {
          setIsValidationPassed(false);
          // make all paths like string
          const allErrorPaths = getAllErrorPaths(errors);

          setSelectedSections(allErrorPaths);

          if (allErrorPaths.length) {
            // each nested accordion use 200 ms
            // and subtract 1 to exclude base nesting levels
            // use Math.max for save positive result
            const timeToScroll =
              Math.max(allErrorPaths[0].split('.').length - 1 || 1) * 300;

            setTimeout(() => scrollToError(allErrorPaths[0]), timeToScroll);
          }
        }
      )();
    }, 0);
  };

  return (
    <BootstrapDialog
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          closeHandler();
        }
      }}
      aria-labelledby="view-incident-modal"
      open={!!editIncident}
      closeAfterTransition
      onTransitionExited={onTransitionExitedHandler}
    >
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeHandler}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#212121',
            zIndex: '999',
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isStructureLoading && (
          <Box
            style={{
              position: 'absolute',
              top: '35px',
              left: 0,
              height: 'calc(100% - 84px)',
              width: '100%',
              zIndex: '999',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid
            item
            xs={3}
            sx={{
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
              pr: 2,
              scrollBehavior: 'smooth',
            }}
          >
            <Box
              sx={{
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#F3F3F3',
                borderRadius: '12px',
                width: 'fit-content',
                height: 51,
              }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '20px',
                  mr: 2,
                }}
              >
                Incident
              </Typography>
              <Typography
                sx={{ fontSize: '18px', fontWeight: 600, lineHeight: '20px' }}
              >
                {selectedIncidentInfo?.dispatch.incident_number}
              </Typography>
            </Box>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuItemClick(BASE_FIRST_ID)}
                  sx={{
                    height: '40px',
                    ...getActiveMenuStyles(selectedSections, BASE_FIRST_ID),
                  }}
                >
                  <ListItemText primary="Base Report Info " />
                </ListItemButton>
              </ListItem>
              {localSettings?.flat().map((item) => {
                const shouldShowBlock = createShouldShowField(
                  form.getValues,
                  item.name
                )(item);
                if (!shouldShowBlock) {
                  return;
                }

                return (
                  <ListItem disablePadding key={item.name}>
                    <ListItemButton
                      onClick={() => handleMenuItemClick(item.name)}
                      sx={{
                        height: '40px',
                        ...getActiveMenuStyles(selectedSections, item.name),
                      }}
                    >
                      <ListItemText
                        primary={capitalizeFirstLetter(item.label || item.name)}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Grid>
          <Grid item xs={9}>
            {formSettingsFetching ? (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            ) : (
              !!selectedIncidentInfo && (
                <>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Accordion id={BASE_FIRST_ID} defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        id={`${BASE_FIRST_ID}-accordion`}
                        sx={{
                          p: 3,
                        }}
                      >
                        <Typography className="title">
                          Base Report Info
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <BaseInfo selectedIncidentInfo={selectedIncidentInfo} />
                      </AccordionDetails>
                    </Accordion>
                    <IncidentContext.Provider value={selectedIncidentInfo}>
                      <FormProvider {...form}>
                        {localSettings?.flat().map((item) => {
                          const expanded = checkIsExpandedAccordion(
                            selectedSections,
                            item.name
                          );
                          const shouldShowBlock = createShouldShowField(
                            form.getValues,
                            item.name
                          )(item);

                          if (!shouldShowBlock) {
                            return null;
                          }

                          return (
                            <Accordion
                              key={item.name}
                              id={item.name}
                              expanded={expanded}
                              onChange={(_, isExpanded) => {
                                onToggleExpand(item.name, isExpanded);
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                id={item.name}
                                sx={{
                                  p: 3,
                                }}
                              >
                                <Typography className="title">
                                  {capitalizeFirstLetter(
                                    item.label || item.name
                                  )}
                                </Typography>
                              </AccordionSummary>

                              <AccordionDetails
                                onClick={() => {
                                  //set the current item as active
                                  onToggleExpand(item.name, true);
                                }}
                              >
                                <FormSection
                                  form={form}
                                  formSettingsItem={item}
                                  expandedSections={selectedSections}
                                  onToggleExpand={onToggleExpand}
                                />
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </FormProvider>
                    </IncidentContext.Provider>
                  </form>
                </>
              )
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          className={`actionButton ${isValidationPassed ? 'success' : ''}`}
          onClick={handleValidation}
        >
          {isValidationPassed ? 'Validation Successful' : 'Validate Form'}
        </Button>
        <Box>
          <Button
            variant="outlined"
            className="actionButton"
            onClick={() => setEditIncident(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="actionButton ms-2"
            onClick={() => {
              handleValidation();
              handleSubmit(onSubmit)();
            }}
            disabled={formSaveFetching}
            startIcon={
              formSaveFetching ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {formSaveFetching ? 'Saving...' : 'Save Report'}
          </Button>
        </Box>
      </DialogActions>
    </BootstrapDialog>
  );
}
